import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  saveTrainerQuestions,
  saveGameSettings,
  loadTrainerVideo,
  loadTrainerImage,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";
import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";
import "./mobile-editor.css";

const { addMessage, showDialog } = userSlice;

function createEmptyQuestion() {
  const id = `q-${Date.now()}`;
  return {
    id,
    questionMode: "text",
    questionText: "",
    hasVideo: false,
    hasImage: false,
    screenpath: "",
    imagepath: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

function hasQuestionContent(question) {
  return Boolean(
    question?.questionText?.trim() || question?.hasVideo || question?.hasImage,
  );
}

export default function MobileTrainerEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;

  const [questions, setQuestions] = useState(game?.trainerQuestions ?? []);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const questionsSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  const currentQuestion = questions[currentQuestionIndex] || null;

  // Auto-save
  useEffect(() => {
    if (!game?._id) return;

    const data = JSON.stringify(questions);
    if (data === lastSavedRef.current) return;

    clearTimeout(questionsSaveTimerRef.current);
    questionsSaveTimerRef.current = setTimeout(() => {
      dispatch(saveTrainerQuestions({ gameId: game._id, questions }))
        .unwrap()
        .then(() => {
          lastSavedRef.current = data;
        })
        .catch(() => {
          dispatch(addMessage({ type: "error", message: "Ошибка сохранения" }));
        });
    }, 1500);

    return () => clearTimeout(questionsSaveTimerRef.current);
  }, [questions, game?._id, dispatch]);

  const handleAddQuestion = () => {
    const newQuestion = createEmptyQuestion();
    setQuestions([...questions, newQuestion]);
    setCurrentQuestionIndex(questions.length);
  };

  const handleDeleteQuestion = () => {
    if (questions.length <= 1) {
      dispatch(
        addMessage({
          type: "error",
          message: "Должен быть минимум один вопрос",
        }),
      );
      return;
    }

    const updatedQuestions = questions.filter(
      (_, idx) => idx !== currentQuestionIndex,
    );
    setQuestions(updatedQuestions);

    if (currentQuestionIndex > updatedQuestions.length - 1) {
      setCurrentQuestionIndex(Math.max(0, updatedQuestions.length - 1));
    }

    setShowDeleteConfirm(null);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const updateQuestion = useCallback(
    (updates) => {
      setQuestions((prevQuestions) =>
        prevQuestions.map((q, idx) =>
          idx === currentQuestionIndex ? { ...q, ...updates } : q,
        ),
      );
    },
    [currentQuestionIndex],
  );

  const updateAnswer = useCallback(
    (answerIndex, updates) => {
      if (!currentQuestion) return;
      const updatedAnswers = currentQuestion.answers.map((a, idx) =>
        idx === answerIndex ? { ...a, ...updates } : a,
      );
      updateQuestion({ answers: updatedAnswers });
    },
    [currentQuestion, updateQuestion],
  );

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !game?._id || !currentQuestion) return;

    setIsLoadingVideo(true);
    try {
      const result = await dispatch(
        loadTrainerVideo({
          gameId: game._id,
          questionId: currentQuestion.id,
          video: file,
        }),
      ).unwrap();

      updateQuestion({
        hasVideo: true,
        screenpath: result.screenpath,
        questionMode: "video",
      });
    } catch {
      dispatch(addMessage({ type: "error", message: "Ошибка загрузки видео" }));
    } finally {
      setIsLoadingVideo(false);
      event.target.value = "";
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !game?._id || !currentQuestion) return;

    setIsLoadingImage(true);
    try {
      const result = await dispatch(
        loadTrainerImage({
          gameId: game._id,
          questionId: currentQuestion.id,
          image: file,
        }),
      ).unwrap();

      updateQuestion({
        hasImage: true,
        imagepath: result.imagepath,
        questionMode: "image",
      });
    } catch {
      dispatch(
        addMessage({ type: "error", message: "Ошибка загрузки изображения" }),
      );
    } finally {
      setIsLoadingImage(false);
      event.target.value = "";
    }
  };

  if (!currentQuestion) {
    return (
      <div className="mobile-editor__empty">
        <p>Нет вопросов для редактирования</p>
        <Button variant="contained" onClick={handleAddQuestion}>
          Добавить вопрос
        </Button>
      </div>
    );
  }

  return (
    <div className="mobile-editor mobile-editor--trainer">
      {/* Навигация по вопросам */}
      <div className="mobile-editor__header">
        <div className="mobile-editor__title">
          <h2>
            Вопрос {currentQuestionIndex + 1} из {questions.length}
          </h2>
        </div>

        <div className="mobile-editor__nav">
          <IconButton
            onClick={handlePrevQuestion}
            disabled={currentQuestionIndex === 0}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="mobile-editor__page">
            {currentQuestionIndex + 1}/{questions.length}
          </span>
          <IconButton
            onClick={handleNextQuestion}
            disabled={currentQuestionIndex === questions.length - 1}
            size="small"
          >
            <ArrowForwardIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowSettings(true)}
            size="small"
            title="Настройки проекта"
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </div>
      </div>

      {/* Редактор вопроса */}
      <Card className="mobile-editor__card">
        <CardContent className="mobile-editor__question-content">
          {/* Режим вопроса */}
          <div className="mobile-editor__mode-selector">
            <label className="mobile-editor__mode-label">Формат:</label>
            <ToggleButtonGroup
              fullWidth
              value={currentQuestion.questionMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode) {
                  updateQuestion({ questionMode: newMode });
                }
              }}
              size="small"
            >
              <ToggleButton value="text">
                <TextFieldsOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="video">
                <VideocamOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="image">
                <ImageOutlinedIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          {/* Текст вопроса */}
          {currentQuestion.questionMode === "text" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Текст вопроса"
              value={currentQuestion.questionText}
              onChange={(e) => updateQuestion({ questionText: e.target.value })}
              className="mobile-editor__field"
              margin="normal"
              placeholder="Введите текст вопроса"
            />
          )}

          {/* Видео */}
          {currentQuestion.questionMode === "video" && (
            <>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Описание видео-вопроса (опционально)"
                value={currentQuestion.questionText}
                onChange={(e) =>
                  updateQuestion({ questionText: e.target.value })
                }
                className="mobile-editor__field"
                margin="normal"
              />

              {currentQuestion.hasVideo ? (
                <div className="mobile-editor__video-preview">
                  <div className="mobile-editor__video-placeholder">
                    <VideocamOutlinedIcon sx={{ fontSize: 48 }} />
                    <p>Видео загружено</p>
                  </div>
                  <Button
                    size="small"
                    startIcon={<VideocamOutlinedIcon />}
                    onClick={() =>
                      document.getElementById("video-input").click()
                    }
                  >
                    Изменить видео
                  </Button>
                  <input
                    id="video-input"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <InputFileUpload
                  onChange={handleVideoUpload}
                  accept="video/*"
                  label="Загрузить видео"
                  disabled={isLoadingVideo}
                />
              )}
            </>
          )}

          {/* Изображение */}
          {currentQuestion.questionMode === "image" && (
            <>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Описание изображения (опционально)"
                value={currentQuestion.questionText}
                onChange={(e) =>
                  updateQuestion({ questionText: e.target.value })
                }
                className="mobile-editor__field"
                margin="normal"
              />

              {currentQuestion.hasImage ? (
                <div className="mobile-editor__image-preview">
                  <div className="mobile-editor__image-placeholder">
                    <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                    <p>Изображение загружено</p>
                  </div>
                  <Button
                    size="small"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() =>
                      document.getElementById("image-input").click()
                    }
                  >
                    Изменить изображение
                  </Button>
                  <input
                    id="image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <InputFileUpload
                  onChange={handleImageUpload}
                  accept="image/*"
                  label="Загрузить изображение"
                  disabled={isLoadingImage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Варианты ответов */}
      <div className="mobile-editor__answers-section">
        <h3 className="mobile-editor__section-title">
          Варианты ответов ({currentQuestion.answers.length})
        </h3>

        {currentQuestion.answers.map((answer, answerIndex) => (
          <Card key={answer.id} className="mobile-editor__answer-item">
            <CardContent className="mobile-editor__answer-content">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={answer.isCorrect}
                    onChange={(e) =>
                      updateAnswer(answerIndex, {
                        isCorrect: e.target.checked,
                      })
                    }
                  />
                }
                label="Правильный ответ"
                className="mobile-editor__answer-checkbox"
              />
              <TextField
                fullWidth
                size="small"
                label={`Ответ ${answerIndex + 1}`}
                value={answer.label}
                onChange={(e) =>
                  updateAnswer(answerIndex, {
                    label: e.target.value,
                  })
                }
                margin="normal"
              />
              {currentQuestion.answers.length > 2 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedAnswers = currentQuestion.answers.filter(
                      (_, idx) => idx !== answerIndex,
                    );
                    updateQuestion({ answers: updatedAnswers });
                  }}
                  color="error"
                  className="mobile-editor__delete-answer"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </CardContent>
          </Card>
        ))}

        {currentQuestion.answers.length < 5 && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              const newAnswer = {
                id: `${currentQuestion.id}-a${Date.now()}`,
                label: "",
                isCorrect: false,
              };
              updateQuestion({
                answers: [...currentQuestion.answers, newAnswer],
              });
            }}
            className="mobile-editor__btn-add-answer"
          >
            Добавить ответ
          </Button>
        )}
      </div>

      {/* Управление вопросами */}
      <div className="mobile-editor__question-actions">
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
        >
          Добавить вопрос
        </Button>
        {questions.length > 1 && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setShowDeleteConfirm("question")}
          >
            Удалить вопрос
          </Button>
        )}
      </div>

      {/* Dialog: Confirm delete question */}
      <Dialog
        open={showDeleteConfirm === "question"}
        onClose={() => setShowDeleteConfirm(null)}
      >
        <DialogTitle>Удалить вопрос?</DialogTitle>
        <DialogContent>
          <p>
            Вы уверены, что хотите удалить этот вопрос? Это действие нельзя
            отменить.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(null)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteQuestion}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Settings */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Настройки проекта</DialogTitle>
        <DialogContent>
          <p>
            Для изменения полных настроек проекта перейдите в настройки на
            десктопной версии.
          </p>
          <p style={{ fontSize: "0.9em", color: "#666" }}>
            В мобильной версии доступно редактирование вопросов и ответов.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
